import { useCallback, useEffect, useMemo, useState } from "react";

const EXTENSION_ID = "Autodesk.VisualClusters";
const DEFAULT_ATTRIBUTE = "Category";
// Synthetic grouping built from the object tree in useForgeView, not a propDb attribute the extension can query.
const NON_PROPDB_ATTRIBUTES = new Set(["Family Name"]);
// Above this the layout becomes unreadable and slow (IDs, marks, comments, ...).
const MAX_CLUSTERS = 100;

// Lets the Autodesk.VisualClusters toolbar button cluster by any model property instead of only Category.
// `propertyData` is useForgeView's modelPropertyData ({ [propertyName]: { [value]: dbIds[] } }).
const useVisualClusters = (viewer, propertyData) => {
  const [extension, setExtension] = useState();
  const [isClustered, setIsClustered] = useState(false);
  const [attribute, setAttributeState] = useState(DEFAULT_ATTRIBUTE);

  // The extension loads asynchronously after viewer creation.
  useEffect(() => {
    const Events = window.Autodesk?.Viewing;
    if (!viewer || !Events) {
      setExtension(undefined);
      return;
    }
    const syncExtension = () => setExtension(viewer.getExtension(EXTENSION_ID) || undefined);
    syncExtension();
    viewer.addEventListener(Events.EXTENSION_LOADED_EVENT, syncExtension);
    return () => viewer.removeEventListener(Events.EXTENSION_LOADED_EVENT, syncExtension);
  }, [viewer]);

  useEffect(() => {
    const Events = window.Autodesk?.Viewing;
    if (!viewer || !extension || !Events) return;

    extension.options.attribName = DEFAULT_ATTRIBUTE;
    setAttributeState(DEFAULT_ATTRIBUTE);

    // The extension hardcodes "Form Clusters by Category" as the button tooltip.
    extension.updateButton = function () {
      Object.getPrototypeOf(this).updateButton.call(this);
      const button = this.clusterButton;
      if (button && button.getState() !== Events.UI.Button.State.DISABLED) {
        button.setToolTip(`Form Clusters by ${this.options.attribName}`);
      }
    };
    extension.updateButton();

    // Fired by the extension when clustering is toggled on (after layout is computed) or off.
    const syncClustered = () => setIsClustered(extension.layoutActive);
    // The extension resets itself when the last model is removed (urn/view switch); the next model may lack the attribute.
    const onModelRemoved = () => {
      if (viewer.getVisibleModels().length) return;
      extension.options.attribName = DEFAULT_ATTRIBUTE;
      extension.updateButton();
      setAttributeState(DEFAULT_ATTRIBUTE);
      setIsClustered(false);
    };
    viewer.addEventListener(Events.TRANSITION_STARTED, syncClustered);
    viewer.addEventListener(Events.MODEL_REMOVED_EVENT, onModelRemoved);

    return () => {
      viewer.removeEventListener(Events.TRANSITION_STARTED, syncClustered);
      viewer.removeEventListener(Events.MODEL_REMOVED_EVENT, onModelRemoved);
      delete extension.updateButton;
    };
  }, [viewer, extension]);

  const setAttribute = useCallback((nextAttribute) => {
    if (!extension || nextAttribute === extension.options.attribName) return;
    extension.options.attribName = nextAttribute;
    extension.updateButton();
    setAttributeState(nextAttribute);
    // setLayoutActive is a no-op when the active flag doesn't change, so toggle to re-run the layout.
    if (extension.layoutActive) {
      extension.setLayoutActive(false);
      extension.setLayoutActive(true, nextAttribute);
    }
  }, [extension]);

  const attributeOptions = useMemo(() => (
    Object.entries(propertyData || {})
      .filter(([name, groups]) => {
        const clusterCount = Object.keys(groups).length;
        return !NON_PROPDB_ATTRIBUTES.has(name) && clusterCount > 1 && clusterCount <= MAX_CLUSTERS;
      })
      .map(([name]) => name)
      .sort((a, b) => a.localeCompare(b))
  ), [propertyData]);

  return { isClustered, attribute, setAttribute, attributeOptions };
};

export default useVisualClusters;
